import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Api, Http } from 'src/api';
import { authQueries, useProfileQuery } from 'src/api/queries';
import { AuthLoginRequest, AuthLoginResponse } from 'src/api/services/AuthClient';
import { Broadcast } from 'src/broadcast';
import { pushNotification } from 'src/lib/services/push-notification';
import { useUserGuiding } from 'src/lib/services/user-guiding';
import { joinExistingValues } from 'src/helpers/joinExistingValues';
import { JsonWrapper } from '../JsonWrapper';
import { AuthJwtObject, jwtStorageKey } from './Auth.types';
import { AuthUtils } from './Auth.utils';
import { isAuthJwtObject } from './Auth.guards';
import { appRoutes } from 'src/routes/routes.types';
import { useBreakpoint } from 'src/lib/hooks';
import { usePreLoginAuthLocation } from 'src/routes/use-pre-login-auth-location';
import { removeFromLocalStorage } from 'src/lib/services/api-query-params/use-sorts';
import { useActivityDetection } from 'src/hooks/useActivityDetection';
import { useCrossTabSessionSync } from 'src/hooks/useCrossTabSessionSync';
import { DEFAULT_SESSION_CONFIG } from 'src/services/Session';

let timeoutId: NodeJS.Timeout | null = null;

const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const { getLocation } = usePreLoginAuthLocation();
  const location = useLocation();
  const userGuiding = useUserGuiding();

  const query = useProfileQuery({ enabled: false });

  // Dual timeout system state
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [warningSecondsRemaining, setWarningSecondsRemaining] = useState(
    DEFAULT_SESSION_CONFIG.warningDurationMinutes * 60
  );
  const inactivityCheckIntervalRef = useRef<number | null>(null);
  const sessionStartTimeRef = useRef<number>(Date.now());

  // Activity detection hook
  const { lastActivity, triggerActivity } = useActivityDetection({
    events: DEFAULT_SESSION_CONFIG.activityEvents,
    throttleMs: DEFAULT_SESSION_CONFIG.activityThrottleMs,
    enabled: query.isSuccess,
  });

  // Cross-tab session sync
  const { broadcastActivity, broadcastLogout, broadcastExtended } = useCrossTabSessionSync({
    enabled: query.isSuccess,
    onLogoutInOtherTab: async () => {
      await endSession();
      navigate(appRoutes.login);
    },
    onActivityInOtherTab: (timestamp) => {
      triggerActivity();
    },
    onExtendedInOtherTab: () => {
      setShowSessionWarning(false);
      triggerActivity();
    },
  });

  const isLoaded = query.isSuccess || query.isError,
    isLoading = query.isLoading,
    isInitialLoading = query.isInitialLoading;

  const startSession = async (jwt: string | AuthJwtObject) => {
    const token = isAuthJwtObject(jwt) ? jwt : AuthUtils.make(jwt);

    AuthUtils.setJwt(token);
    Http.setBearer(token.token);
    if (document.visibilityState !== 'hidden') {
      Broadcast.user.connect(token.token);
    }
    if (pushNotification.isSupported()) {
      pushNotification.init();
    }

    // Fetch profile with timeout to prevent infinite hanging
    // Fixes ADMIN-WEB-APP-E2, ADMIN-WEB-APP-E3
    const PROFILE_FETCH_TIMEOUT = 10000; // 10 seconds
    let profileTimeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const profilePromise = queryClient.fetchQuery({ queryKey: query.queryKey, queryFn: query.queryFn });
      const timeoutPromise = new Promise<never>((_, reject) => {
        profileTimeoutId = setTimeout(() => reject(new Error('Profile fetch timeout')), PROFILE_FETCH_TIMEOUT);
      });

      await Promise.race([profilePromise, timeoutPromise]);
    } catch (error) {
      console.warn('Profile fetch failed or timed out, continuing session start:', error);
    } finally {
      if (profileTimeoutId) clearTimeout(profileTimeoutId);
    }

    // Load UserGuiding SDK and identify the admin user
    userGuiding.loadAndIdentify().catch((e) => console.error('Failed to load user guiding SDK', e));

    // Reset session tracking for dual timeout
    sessionStartTimeRef.current = Date.now();
    triggerActivity();
    broadcastActivity();
  };

  const endSession = async () => {
    await Promise.all(
      Object.values(authQueries).map(async (queryKey) => {
        await queryClient.cancelQueries({ queryKey: [queryKey] });
        queryClient.removeQueries({ queryKey: [queryKey] });
      }),
    );

    Http.setBearer('');
    Broadcast.user.disconnect();
    userGuiding.shutdown();

    AuthUtils.removeJwt();

    // Cleanup dual timeout system
    if (inactivityCheckIntervalRef.current !== null) {
      clearInterval(inactivityCheckIntervalRef.current);
      inactivityCheckIntervalRef.current = null;
    }
    setShowSessionWarning(false);
  };

  const login = async (data: AuthLoginRequest): Promise<AuthLoginResponse> => {
    const response = await Api.auth.login(data);

    // Check if 2FA is required
    if (response.requires_2fa) {
      return response; // Return the 2FA response to handle in the component
    }

    // If we have an access token, start the session
    if (response.access_token) {
      await startSession(response.access_token);
    }

    return response;
  };

  const logout = async (): Promise<void> => {
    await Api.auth.logout();

    await endSession();
    broadcastLogout();
    try {
      removeFromLocalStorage('requests-table');
    } catch (_e) {}
  };

  const verify2FA = async (code: string, email: string): Promise<AuthLoginResponse> => {
    try {
      const response = await Api.auth.verify2FA({ code, email });
      
      if (response.access_token) {
        await startSession(response.access_token);
      }
      
      return response;
    } catch (error: any) {
      // Always throw user-friendly message instead of backend error
      throw new Error('Invalid verification code');
    }
  };

  const resend2FA = async (email: string): Promise<{ message: string }> => {
    return await Api.auth.resend2FA({ email });
  };

  const refresh = async () => {
    if (AuthUtils.isExpired()) {
      AuthUtils.removeJwt();

      return;
    }

    await Api.auth
      .refresh()
      .then(async ({ access_token }) => {
        if (!access_token) {
          await endSession();

          return;
        }
        await startSession(access_token);
      })
      .catch(async () => await endSession());
  };

  const handleStorageTokenChange = async (e: StorageEvent): Promise<void> => {
    if (e.key !== jwtStorageKey) {
      return;
    }

    if (e.oldValue && !e.newValue) {
      // Means user has logged out
      await endSession();
      try {
        removeFromLocalStorage('requests-table');
      } catch (_e) {}
      navigate(appRoutes.login);
      return;
    }

    const newJwt = JsonWrapper.toJson(e.newValue);
    if (!isAuthJwtObject(newJwt)) {
      return;
    }

    if (!e.oldValue) {
      // Means user has logged in
      await startSession(newJwt);
      navigate(getLocation(location.state?.from) ?? appRoutes.profile, {
        replace: true,
      });
      return;
    }

    if (e.oldValue && e.newValue) {
      // Means token has been refreshed, probably

      const oldJwt = JsonWrapper.toJson(e.oldValue);
      if (
        isAuthJwtObject(oldJwt) &&
        !AuthUtils.isExpired(newJwt) &&
        oldJwt.token !== newJwt.token
      ) {
        await startSession(newJwt);
      }
    }
  };

  const handleVisibilityChange = async () => {
    if (document.visibilityState === 'hidden') {
      // Increased from 5 seconds to 15 minutes to prevent disconnection on short tab switches
      // This fixes the "Server Error" issue when users switch tabs for 2-3 minutes
      timeoutId = setTimeout(() => {
        Broadcast.user.disconnect();
      }, 15 * 60 * 1000); // 15 minutes

      return;
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (!Broadcast.user.isConnected() && !AuthUtils.isExpired()) {
      Broadcast.user.connect(AuthUtils.getJwt().token);
      queryClient.invalidateQueries({
        queryKey: [authQueries.notifications],
      });
      queryClient.invalidateQueries({
        queryKey: [authQueries.unreadNotifications],
      });
      queryClient.invalidateQueries({
        queryKey: [authQueries.requestCounts],
      });
    }
  };

  const mount = () => {
    window.addEventListener('storage', handleStorageTokenChange);
    window.addEventListener('visibilitychange', handleVisibilityChange);
  };

  const dismount = () => {
    window.removeEventListener('storage', handleStorageTokenChange);
    window.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  // Handler for session warning modal - extend session
  const handleExtendSession = () => {
    console.log('[useAuth] Session extended by user');
    setShowSessionWarning(false);
    triggerActivity();
    broadcastExtended();
  };

  // Handler for session warning modal - logout
  const handleLogoutFromWarning = async () => {
    console.log('[useAuth] User chose to logout from warning');
    await logout();
    navigate(appRoutes.login);
  };

  // Monitor inactivity and absolute session timeout
  useEffect(() => {
    if (!query.isSuccess) return;

    // Check inactivity every second
    inactivityCheckIntervalRef.current = window.setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const timeSinceSessionStart = now - sessionStartTimeRef.current;

      const inactivityThreshold = DEFAULT_SESSION_CONFIG.idleTimeoutMinutes * 60 * 1000;
      const warningThreshold = inactivityThreshold - (DEFAULT_SESSION_CONFIG.warningDurationMinutes * 60 * 1000);
      const absoluteThreshold = DEFAULT_SESSION_CONFIG.maxSessionDurationMinutes * 60 * 1000;

      // Check absolute session timeout (7 days)
      if (timeSinceSessionStart >= absoluteThreshold) {
        console.log('[useAuth] Absolute session timeout reached (7 days) - logging out');
        logout().then(() => navigate(appRoutes.login));
        return;
      }

      // Check inactivity timeout
      if (timeSinceActivity >= inactivityThreshold) {
        if (!showSessionWarning) {
          console.log('[useAuth] Inactivity timeout reached - auto logout');
          logout().then(() => navigate(appRoutes.login));
        }
        return;
      }

      // Show warning before inactivity timeout
      if (timeSinceActivity >= warningThreshold && !showSessionWarning) {
        const secondsUntilLogout = Math.floor((inactivityThreshold - timeSinceActivity) / 1000);
        console.log(`[useAuth] Showing inactivity warning (${Math.floor(timeSinceActivity / 60000)} min idle)`);
        setWarningSecondsRemaining(secondsUntilLogout);
        setShowSessionWarning(true);
        return;
      }

      // Update countdown if warning is shown
      if (showSessionWarning) {
        const secondsUntilLogout = Math.floor((inactivityThreshold - timeSinceActivity) / 1000);
        setWarningSecondsRemaining(Math.max(0, secondsUntilLogout));
      }
    }, 1000);

    return () => {
      if (inactivityCheckIntervalRef.current !== null) {
        clearInterval(inactivityCheckIntervalRef.current);
        inactivityCheckIntervalRef.current = null;
      }
    };
  }, [query.isSuccess, lastActivity, showSessionWarning, logout, navigate]);

  // Broadcast activity to other tabs when local activity detected
  useEffect(() => {
    if (query.isSuccess && lastActivity) {
      broadcastActivity(lastActivity);
    }
  }, [lastActivity, query.isSuccess, broadcastActivity]);

  return {
    isLoaded,
    isLoading,
    isInitialLoading,
    login,
    logout,
    verify2FA,
    resend2FA,
    refresh,
    startSession,
    endSession,
    mount,
    dismount,
    // Dual timeout system
    showSessionWarning,
    warningSecondsRemaining,
    handleExtendSession,
    handleLogoutFromWarning,
  };
};

export { useAuth };
